import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  AvailablePieceJointeDto,
  ComputedResponseDtoListAvailableFilesEnum,
  DossierDtoAdminFonctionnel,
  DossierDtoAdminFonctionnelActionsDisponiblesEnum,
} from 'lib_api/lib/api/gen';

import ActionAndFileWrapperAdmin from 'components/ActionAndFileWrapper/ActionAndFileWrapperAdmin';
import { DeletePieceJointeModal } from 'components/ActionAndFileWrapper/AvailableFiles/components/DeletePieceJointeModal';
import { AvailableAction } from 'components/ActionAndFileWrapper/types';
import AsyncComponent from 'components/AsyncComponent/AsyncComponent';
import BaseModal from 'components/WrappedComponents/BaseModal/BaseModal';
import { useAdminFonctionnelFilterContext } from 'hooks/AdminFonctionnelFilterStoreContext';
import { useApi } from 'hooks/ApiStoreContext';
import { useDossierAdminContext } from 'hooks/dossierAdmin/DossierAdminContext';
import useFetchProcessInstances from 'hooks/dossierAdmin/useFetchProcessInstances';
import { useDeletePieceJointe } from 'hooks/dossiers/useDeletePieceJointe';
import { useDownloadDossierAdminFonctionnel } from 'hooks/dossiers/useDownloadDossierAdminFonctionnel';
import { useEnvironnement } from 'hooks/EnvironementStoreContext';
import { useModal } from 'hooks/modal/useModal';
import { useModalWithValue } from 'hooks/modal/useModalWithValue';
import { useFetchArPndNotification } from 'hooks/notification/useFetchArPndNotification';
import { useFetchCourrierNotification } from 'hooks/notification/useFetchCourrierNotification';
import { useDisplayActionsRequisesBannerAdmin } from 'hooks/useActionsRequises';
import { ActionsRequisesLinkState } from 'utils/actionsRequisesUtils';
import { BaseModalWidthUtils } from 'utils/BaseModalWidthUtils';

import ControleBlocageVente from '../ControleBlocageVente/ControleBlocageVente';
import ControleBlocageVenteDoublon from '../ControleBlocageVente/ContrôleBlocageVenteDoublon';

import FixDataNotificationManuelle from './FixDataNotificationManuelle/FixDataNotificationManuelle';
import ImporterPieceJointeAdminFonctionnel from './ImporterPieceJointe/ImporterPieceJointeAdminFonctionnel';
import RenseignerInformationsSiv from './RenseignerInformationsSiv/RenseignerInformationsSiv';
import UpdateClassement from './UpdateClassement/UpdateClassement';
import UpdateDonneesJudiciairesAdminFonctionnel from './UpdateDonneesJudiciaires/UpdateDonneesJudiciairesAdminFonctionnel';
import UpdateDonneesVehiculeAdminFonctionnel from './UpdateDonneesVehicule/UpdateDonneesVehiculeAdminFonctionnel';
import UpdateEntiteRattachementAdmin from './UpdateEntitesRattachements/UpdateEntiteRattachementAdmin';
import UpdateSortie from './UpdateSortie/UpdateSortie';
import { ModalConfirmationAnnulationSortie } from './ModalConfirmationAnnulationSortie';
import { ModalSuppressionDossier } from './ModalSuppressionDossierProps';

type ActionMapping = {
  [key in DossierDtoAdminFonctionnelActionsDisponiblesEnum]?: AvailableAction;
};

const getValueOrEmptyList = <T,>(value?: T): T[] => (value ? [value] : []);

const ActionsSuiviVehicule = (): React.ReactElement => {
  const { state } = useLocation() as { state: ActionsRequisesLinkState };
  const { AdminFonctionnelControllerApi } = useApi();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [{ result: dossierAdmin, setDossier }, { fetchEvents }] =
    useDossierAdminContext();
  const [, , adminActionsRequises] = useAdminFonctionnelFilterContext();
  const download = useDownloadDossierAdminFonctionnel(
    setIsLoading,
    dossierAdmin.id,
    dossierAdmin.immatriculation,
  );

  function handleDeletePieceJointe(pieceJointe: AvailablePieceJointeDto) {
    deletePieceJointeModal.open(pieceJointe);
  }

  const { processInstances, errorOccured, fetchInstances } =
    useFetchProcessInstances(dossierAdmin.id);

  const { fetchCourrierNotification } = useFetchCourrierNotification(
    AdminFonctionnelControllerApi,
    dossierAdmin.id,
    (dossier: DossierDtoAdminFonctionnel) => {
      void fetchEvents();
      setDossier(dossier);
    },
  );

  const { fetchArPndNotification } = useFetchArPndNotification(
    AdminFonctionnelControllerApi,
    dossierAdmin.id,
    (dossier: DossierDtoAdminFonctionnel) => {
      void fetchEvents();
      setDossier(dossier);
    },
  );

  useDisplayActionsRequisesBannerAdmin(dossierAdmin, adminActionsRequises);

  const updateEntiteRattachementModal = useModal();
  const cancelSortieModal = useModal();
  const deleteDossierModal = useModal();
  const updateDonneesVehicule = useModal();
  const updateDonneesJudiciaires = useModal();
  const updateDonneesVehiculeJustice = useModal();
  const updateSortieModal = useModal();
  const updateClassementModal = useModal();
  const fixDataNotificationManuelle = useModal();
  const controleBlocageVente = useModal(
    state?.openControleBlocageVenteImmoPJ ||
      state?.openControleBlocageVenteOpposition ||
      false,
  );
  const controleBlocageVenteDoublon = useModal(
    state?.openControleBlocageVenteDoublon || false,
  );
  const renseignerInformationsSiv = useModal();
  const importerPieceJointe = useModal();
  const deletePieceJointeModal = useModalWithValue();

  const pieceJointe = deletePieceJointeModal.value;

  const deletePieceJointe = useDeletePieceJointe(
    setIsLoading,
    dossierAdmin,
    setDossier,
    deletePieceJointeModal.close,
    fetchEvents,
  );

  const [{ JUSTICE_ACTIVE_BOOL }] = useEnvironnement();

  const actionMapping: ActionMapping = useMemo((): {
    [key in DossierDtoAdminFonctionnelActionsDisponiblesEnum]?: AvailableAction;
  } => {
    const updateVehiculeAction =
      dossierAdmin.actionsDisponibles?.includes(
        DossierDtoAdminFonctionnelActionsDisponiblesEnum.UPDATE_DONNEES_VEHICULE_JUSTICE,
      ) && JUSTICE_ACTIVE_BOOL
        ? {
            UPDATE_DONNEES_VEHICULE_JUSTICE: {
              title: "Modifier les données du véhicule et de l'infraction",
              onClick: updateDonneesVehiculeJustice.open,
            },
          }
        : {
            UPDATE_DONNEES_VEHICULE: {
              title: "Modifier les données du véhicule et de l'infraction",
              onClick: updateDonneesVehicule.open,
            },
          };

    return {
      EXPORT_DONNEES_DOSSIER: {
        title: 'Exporter les données du dossier',
        onClick: () => download(),
      },
      UPDATE_ENTITES_RATTACHEMENT_ADMIN: {
        title: 'Modifier les entités de rattachement',
        onClick: updateEntiteRattachementModal.open,
      },
      ANNULATION_SORTIE_VEHICULE: {
        title: 'Annuler la sortie',
        onClick: cancelSortieModal.open,
      },
      MODIFICATION_SORTIE_VEHICULE: {
        title: 'Modifier les données de sortie',
        onClick: updateSortieModal.open,
      },
      SUPPRESSION_DOSSIER: {
        title: 'Supprimer le dossier',
        onClick: deleteDossierModal.open,
      },
      UPDATE_CLASSEMENT: {
        title: 'Modifier le classement',
        onClick: updateClassementModal.open,
      },
      CORRIGER_DONNEES_NOTIFICATION_MANUELLE: {
        title: 'Modifier le suivi de notification',
        onClick: fixDataNotificationManuelle.open,
      },
      RELANCE_VENTE_IMMO_PJ: {
        title: 'Contrôler le blocage de la vente',
        onClick: controleBlocageVente.open,
      },
      RELANCE_VENTE_OPPOSITION: {
        title: 'Contrôler le blocage de la vente',
        onClick: controleBlocageVente.open,
      },
      RELANCE_VENTE_DOUBLON: {
        title: 'Contrôler le blocage de la vente',
        onClick: controleBlocageVenteDoublon.open,
      },
      RECUPERATION_COURRIER_AUTOMATIQUE: {
        title: 'Récupérer le courrier de notification',
        onClick: fetchCourrierNotification,
      },
      RECUPERATION_AR_PND_AUTOMATIQUE: {
        title: "Récupérer l'AR ou le PND de notification",
        onClick: fetchArPndNotification,
      },
      RENSEIGNER_INFORMATIONS_SIV: {
        title: 'Renseigner les informations du SIV',
        onClick: renseignerInformationsSiv.open,
      },
      IMPORTER_PIECES_JOINTES: {
        title: 'Joindre un document',
        onClick: importerPieceJointe.open,
      },
      ...updateVehiculeAction,
      UPDATE_DONNEES_JUDICIAIRE: JUSTICE_ACTIVE_BOOL
        ? {
            title: 'Modifier les données judiciaires',
            onClick: updateDonneesJudiciaires.open,
          }
        : undefined,
    };
  }, [dossierAdmin]);

  const actions: AvailableAction[] =
    dossierAdmin.actionsDisponibles?.flatMap(action =>
      getValueOrEmptyList(actionMapping[action]),
    ) ?? [];

  const availableFiles: ComputedResponseDtoListAvailableFilesEnum[] =
    dossierAdmin.listAvailableFiles?.map(
      value => ComputedResponseDtoListAvailableFilesEnum[value],
    ) ?? [];

  return (
    <>
      <AsyncComponent
        errorOccured={errorOccured}
        inProgress={false}
        reload={fetchInstances}
        render={() => {
          return (
            <ActionAndFileWrapperAdmin
              filesList={availableFiles}
              piecesJointesList={dossierAdmin.listAvailablePiecesJointes ?? []}
              actions={actions}
              dossierId={dossierAdmin.id}
              processInstances={processInstances}
              deletePieceJointe={handleDeletePieceJointe}
            />
          );
        }}
      />
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateEntiteRattachementModal.visible}
        closeModal={updateEntiteRattachementModal.close}
      >
        <UpdateEntiteRattachementAdmin
          closeModal={updateEntiteRattachementModal.close}
        />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateSortieModal.visible}
        closeModal={updateSortieModal.close}
      >
        <UpdateSortie closeModal={updateSortieModal.close} />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateDonneesVehicule.visible}
        closeModal={updateDonneesVehicule.close}
      >
        <UpdateDonneesVehiculeAdminFonctionnel
          closeModal={updateDonneesVehicule.close}
        />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateDonneesVehiculeJustice.visible}
        closeModal={updateDonneesVehiculeJustice.close}
      >
        <p>hello world</p>
      </BaseModal>
      {JUSTICE_ACTIVE_BOOL && (
        <BaseModal
          width={BaseModalWidthUtils.mediumWidth}
          isVisible={updateDonneesJudiciaires.visible}
          closeModal={updateDonneesJudiciaires.close}
        >
          <UpdateDonneesJudiciairesAdminFonctionnel
            closeModal={updateDonneesJudiciaires.close}
          />
        </BaseModal>
      )}
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={updateClassementModal.visible}
        closeModal={updateClassementModal.close}
      >
        <UpdateClassement closeModal={updateClassementModal.close} />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.mediumWidth}
        isVisible={fixDataNotificationManuelle.visible}
        closeModal={fixDataNotificationManuelle.close}
      >
        <FixDataNotificationManuelle
          closeModal={fixDataNotificationManuelle.close}
        />
      </BaseModal>
      <ModalSuppressionDossier
        visible={deleteDossierModal.visible}
        close={deleteDossierModal.close}
        dossierId={dossierAdmin.id}
      />
      <ModalConfirmationAnnulationSortie
        visible={cancelSortieModal.visible}
        close={cancelSortieModal.close}
        dossierId={dossierAdmin.id}
      />
      <BaseModal
        width={BaseModalWidthUtils.largeWidth}
        isVisible={controleBlocageVente.visible}
        closeModal={controleBlocageVente.close}
      >
        <ControleBlocageVente close={controleBlocageVente.close} />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.largeWidth}
        isVisible={controleBlocageVenteDoublon.visible}
        closeModal={controleBlocageVenteDoublon.close}
      >
        <ControleBlocageVenteDoublon
          close={controleBlocageVenteDoublon.close}
        />
      </BaseModal>
      <BaseModal
        title={
          <div className={'centered'}>Renseigner les informations SIV</div>
        }
        width={BaseModalWidthUtils.largeWidth}
        isVisible={renseignerInformationsSiv.visible}
        closeModal={renseignerInformationsSiv.close}
      >
        <RenseignerInformationsSiv close={renseignerInformationsSiv.close} />
      </BaseModal>
      <BaseModal
        width={BaseModalWidthUtils.largeWidth}
        isVisible={importerPieceJointe.visible}
        closeModal={importerPieceJointe.close}
      >
        <ImporterPieceJointeAdminFonctionnel
          closeModal={importerPieceJointe.close}
        />
      </BaseModal>
      {pieceJointe && (
        <DeletePieceJointeModal
          isVisible={deletePieceJointeModal.visible}
          isLoading={isLoading}
          closeModal={deletePieceJointeModal.close}
          confirmDelete={deletePieceJointe}
          pieceJointe={pieceJointe}
          dossier={dossierAdmin}
        />
      )}
    </>
  );
};

export default ActionsSuiviVehicule;
